import { ChartSource } from '../types/charts';
import {
  Music2Icon,
  RadioIcon,
  ShareIcon as ShazamIcon,
  CloudIcon,
  AlignJustifyIcon as SpotifyIcon,
  BarChartIcon,
  AppleIcon,
  YoutubeIcon,
  AudioWaveformIcon as WaveformIcon,
  Music,
  Globe2,
  Users,
  Disc,
  Mic2,
  Headphones,
  LanguagesIcon,
  TrendingUp,
  Video,
  ShoppingBag,
  Store,
  Cross,
  DollarSign,
  Album,
  Guitar,
  Flag
} from 'lucide-react';

export const chartSources: ChartSource[] = [
  {
    id: 'billboard',
    name: 'Billboard Hot 100',
    description: 'The most popular songs across all genres, ranked by radio airplay, sales data, and streaming activity.',
    url: 'https://www.billboard.com/charts/hot-100/',
    logo: Music
  },
  {
    id: 'billboard-200',
    name: 'Billboard 200',
    description: 'The most popular albums across all genres, ranked by album sales and streaming activity.',
    url: 'https://www.billboard.com/charts/billboard-200/',
    logo: Disc
  },
  {
    id: 'billboard-independent-albums',
    name: 'Billboard Independent Albums',
    description: 'The most popular albums from independent record labels, ranked by sales data and streaming activity.',
    url: 'https://www.billboard.com/charts/independent-albums/',
    logo: Store
  },
  {
    id: 'billboard-uk-songs',
    name: 'Billboard UK Songs',
    description: 'The most popular songs in the United Kingdom, ranked by sales and streaming activity data.',
    url: 'https://www.billboard.com/charts/u-k-songs-hotw/',
    logo: Flag
  },
  {
    id: 'billboard-global',
    name: 'Billboard Global 200',
    description: 'The most popular songs globally, ranked by digital sales and streaming data from more than 200 territories.',
    url: 'https://www.billboard.com/charts/billboard-global-200/',
    logo: Globe2
  },
  {
    id: 'billboard-global-excl-us',
    name: 'Billboard Global Excl. US',
    description: "The week's most popular songs based on streaming and sales activity from over 200 territories around the world — excluding the United States.",
    url: 'https://www.billboard.com/charts/billboard-global-excl-us/',
    logo: Globe2
  },
  {
    id: 'billboard-artist',
    name: 'Billboard Artist 100',
    description: 'The most popular artists ranked by album and track sales, radio airplay, streaming, and social media engagement.',
    url: 'https://www.billboard.com/charts/artist-100/',
    logo: Users
  },
  {
    id: 'billboard-vinyl-albums',
    name: 'Billboard Vinyl Albums',
    description: 'The top-selling vinyl albums of the week, ranked by sales data.',
    url: 'https://www.billboard.com/charts/vinyl-albums',
    logo: Album
  },
  {
    id: 'billboard-indie-store-album-sales',
    name: 'Billboard Indie Store Album Sales',
    description: 'Top-selling albums at independent record stores across the United States.',
    url: 'https://www.billboard.com/charts/independent-albums',
    logo: Store
  },
  {
    id: 'billboard-christian-songs',
    name: 'Billboard Hot Christian Songs',
    description: 'The most popular Christian songs, ranked by radio airplay, sales data, and streaming activity.',
    url: 'https://www.billboard.com/charts/christian-songs',
    logo: Cross
  },
  {
    id: 'billboard-digital-song-sales',
    name: 'Billboard Digital Song Sales',
    description: "The week's best-selling digital songs, ranked by sales data.",
    url: 'https://www.billboard.com/charts/digital-song-sales',
    logo: DollarSign
  },
  {
    id: 'billboard-top-album-sales',
    name: 'Billboard Top Album Sales',
    description: "The week's best-selling albums across all formats, ranked by sales data.",
    url: 'https://www.billboard.com/charts/top-album-sales',
    logo: ShoppingBag
  },
  {
    id: 'billboard-hot-country-songs',
    name: 'Billboard Hot Country Songs',
    description: 'The most popular country music songs, ranked by radio airplay, sales data, and streaming activity.',
    url: 'https://www.billboard.com/charts/country-songs/',
    logo: Mic2
  },
  {
    id: 'billboard-hot-dance-electronic-songs',
    name: 'Billboard Hot Dance/Electronic Songs',
    description: 'The most popular dance and electronic music songs, ranked by streaming, sales, and radio airplay.',
    url: 'https://www.billboard.com/charts/dance-electronic-songs/',
    logo: Headphones
  },
  {
    id: 'billboard-hot-latin-songs',
    name: 'Billboard Hot Latin Songs',
    description: 'The most popular Latin music songs across all genres, ranked by radio airplay, sales data, and streaming activity.',
    url: 'https://www.billboard.com/charts/latin-songs/',
    logo: Guitar
  },
  {
    id: 'billboard-emerging-artists',
    name: 'Billboard Emerging Artists',
    description: 'The fastest-rising artists ranked by album and track sales, radio airplay, streaming, and social media engagement.',
    url: 'https://www.billboard.com/charts/emerging-artists/',
    logo: TrendingUp
  },
  {
    id: 'billboard-tiktok-top-50',
    name: 'Billboard TikTok Top 50',
    description: 'The most popular songs on TikTok, ranked by video creations, video views, and user engagement.',
    url: 'https://www.billboard.com/charts/tiktok-billboard-top-50/',
    logo: Video
  },
  {
    id: 'billboard-us-afrobeats-songs',
    name: 'Billboard U.S. Afrobeats Songs',
    description: 'The most popular Afrobeats songs in the United States, ranked by streaming, radio airplay, and sales data.',
    url: 'https://www.billboard.com/charts/billboard-u-s-afrobeats-songs/',
    logo: Music
  },
  {
    id: 'spotify',
    name: 'Spotify Global Top 50',
    url: 'https://charts.spotify.com/charts/overview/global',
    logo: SpotifyIcon,
    description: 'Most streamed songs on Spotify globally.'
  },
  {
    id: 'apple',
    name: 'Apple Music Top 100',
    url: 'https://music.apple.com/us/playlist/top-100-usa/',
    logo: AppleIcon,
    description: 'Top songs on Apple Music in the USA.'
  },
  {
    id: 'youtube',
    name: 'YouTube Music Charts',
    url: 'https://charts.youtube.com/',
    logo: YoutubeIcon,
    description: 'Most viewed music videos and trending songs on YouTube.'
  },
  {
    id: 'shazam',
    name: 'Shazam Top 200',
    url: 'https://www.shazam.com/charts/top-200/united-states',
    logo: ShazamIcon,
    description: 'Most Shazamed songs in the United States.'
  },
  {
    id: 'aria',
    name: 'ARIA Charts',
    url: 'https://www.aria.com.au/charts/',
    logo: Music2Icon,
    description: 'Official Australian music charts.'
  },
  {
    id: 'americana',
    name: 'Americana Radio',
    url: 'https://americanamusic.org/americana-radio/',
    logo: RadioIcon,
    description: 'Top Americana music radio charts.'
  },
  {
    id: 'soundcloud',
    name: 'SoundCloud Top 50',
    url: 'https://soundcloud.com/charts/top',
    logo: CloudIcon,
    description: 'Trending tracks on SoundCloud.'
  },
  {
    id: 'beatport',
    name: 'Beatport Top 100',
    url: 'https://www.beatport.com/top-100',
    logo: WaveformIcon,
    description: 'Top electronic and dance music tracks.'
  },
  {
    id: 'billboard-top-streaming-albums',
    name: 'Billboard Top Streaming Albums',
    description: 'The most-streamed albums of the week in the U.S., ranked by streaming equivalent album units.',
    url: 'https://www.billboard.com/charts/top-streaming-albums/',
    logo: CloudIcon
  }
];